import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleInfo, faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './WebflowPublishNotice.css';

const API = process.env.REACT_APP_API_BASE_URL;

/**
 * Webflow: "changes saved, publish when you are ready".
 *
 * Activation and deactivation only register/remove the agent script on the
 * Webflow site; nothing is published automatically (Webflow Marketplace: the
 * App prompts, the customer decides, because a publish pushes ALL staged
 * changes of their site live). This notice appears whenever the backend
 * reports `needs_publish` for the site and offers the explicit publish.
 *
 * Used on /integration, in the IntegrationModal, in the agent-creation
 * Channels step and in the Webflow onboarding. Pure presentation + one call:
 * POST /api/webflow/publish_site.
 */
export default function WebflowPublishNotice({ botId, siteId, siteName, onPublished, compact = false, inline = false, title, text }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const publish = async () => {
    if (!botId || !siteId || busy) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(
        `${API}webflow/publish_site?bot_id=${encodeURIComponent(botId)}&site_id=${encodeURIComponent(siteId)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setDone(true);
        if (typeof onPublished === 'function') onPublished();
      } else if (r.status === 429) {
        setErr(d.message || t('integration_webflow_rate_limit'));
      } else if (r.status === 403 && d.status === 'token_revoked') {
        setErr(t('wf_publish_reconnect'));
      } else {
        setErr(t('wf_publish_failed'));
      }
    } catch (e) {
      console.error('Webflow publish failed:', e);
      setErr(t('wf_publish_failed'));
    } finally {
      setBusy(false);
    }
  };

  // One line under a site row: what publishing changes, the button, the note
  // that a publish pushes everything staged.
  if (inline) {
    return (
      <div className="wf-publish-inline" role="status">
        {done ? (
          <span className="wf-publish-inline__done">
            <FontAwesomeIcon icon={faCircleCheck} /> {t('wf_publish_done_title')}
          </span>
        ) : (
          <>
            <span className="wf-publish-inline__text">{text}</span>
            <button type="button" className="wf-publish-notice__btn wf-publish-inline__btn" onClick={publish} disabled={busy}>
              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />}
              {t('wf_publish_button')}
            </button>
            <span className="wf-publish-inline__note">{t('wf_publish_all_note')}</span>
          </>
        )}
        {err && <span className="wf-publish-notice__error">{err}</span>}
      </div>
    );
  }

  if (done) {
    return (
      <div className="wf-publish-notice wf-publish-notice--done" role="status">
        <FontAwesomeIcon icon={faCircleCheck} className="wf-publish-notice__icon" />
        <div className="wf-publish-notice__body">
          <strong>{t('wf_publish_done_title')}</strong>
          {!compact && <p>{t('wf_publish_done_text', { siteName: siteName || 'Webflow' })}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="wf-publish-notice" role="status">
      <FontAwesomeIcon icon={faCircleInfo} className="wf-publish-notice__icon" />
      <div className="wf-publish-notice__body">
        <strong>{title || t('wf_publish_title')}</strong>
        <p>{text || t('wf_publish_text', { siteName: siteName || 'Webflow' })}</p>
        <p className="wf-publish-notice__warn">{t('wf_publish_warning')}</p>
        <div className="wf-publish-notice__actions">
          <button type="button" className="wf-publish-notice__btn" onClick={publish} disabled={busy}>
            {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faUpload} />}
            {t('wf_publish_button')}
          </button>
          <span className="wf-publish-notice__alt">{t('wf_publish_alt')}</span>
        </div>
        {err && <p className="wf-publish-notice__error">{err}</p>}
      </div>
    </div>
  );
}
